"use client";

import { useLingui } from "@lingui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnalysisTargetOptionsState } from "./analysis-target-options";
import { selectFallbackPrompts } from "./sample-prompts";

const SAMPLE_PROMPT_COUNT = 4;

type UseSamplePromptsOptions = {
  workspaceId: string;
  lang: string;
  optionsState: AnalysisTargetOptionsState;
};

type UseSamplePromptsResult = {
  prompts: string[];
  isGenerating: boolean;
  refresh: () => void;
};

/**
 * ワークスペースに合わせたサンプルプロンプトを提供する。
 *
 * 初回はドメイン別の静的プールからフォールバック表示し、
 * 並行して `/api/chat/sample-prompts` から LLM 生成結果を取得して差し替える。
 * `refresh` で再生成を要求する。
 */
export function useSamplePrompts({
  workspaceId,
  lang,
  optionsState,
}: UseSamplePromptsOptions): UseSamplePromptsResult {
  const { i18n } = useLingui();
  const abortControllerRef = useRef<AbortController | null>(null);

  const availableDomains = useMemo(() => {
    const options = optionsState.options;
    return {
      hasSA: options.some(
        (o) => o.target.projectType === "SearchAnalyticsProject",
      ),
      hasUA: options.some(
        (o) => o.target.projectType === "UserAnalyticsProject",
      ),
      hasIW: options.some((o) => o.target.projectType === "IndexWorkerProject"),
    };
  }, [optionsState.options]);

  const fallbackPrompts = useMemo(
    () =>
      selectFallbackPrompts(availableDomains, SAMPLE_PROMPT_COUNT).map((desc) =>
        i18n._(desc),
      ),
    [availableDomains, i18n],
  );

  const [generatedPrompts, setGeneratedPrompts] = useState<string[] | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);

  const fetchPrompts = useCallback(async () => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsGenerating(true);
    try {
      const response = await fetch("/api/chat/sample-prompts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId, lang }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        setGeneratedPrompts(null);
        return;
      }

      const data: { prompts: string[] } = await response.json();
      if (
        !abortController.signal.aborted &&
        data.prompts.length === SAMPLE_PROMPT_COUNT
      ) {
        setGeneratedPrompts(data.prompts);
      }
    } catch {
      if (!abortController.signal.aborted) {
        setGeneratedPrompts(null);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsGenerating(false);
      }
    }
  }, [workspaceId, lang]);

  useEffect(() => {
    if (!workspaceId || optionsState.fetching) return;
    void fetchPrompts();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [workspaceId, optionsState.fetching, fetchPrompts]);

  const refresh = useCallback(() => {
    setGeneratedPrompts(null);
    void fetchPrompts();
  }, [fetchPrompts]);

  const prompts = generatedPrompts ?? fallbackPrompts;

  return { prompts, isGenerating, refresh };
}
